/**
 * The AfricaNenda IIPS Sandbox terms of service, as shown on /terms and accepted
 * in the application wizard. The version is recorded with each application, so
 * any change to the wording needs a new TERMS_VERSION.
 *
 * Each section has an id (its anchor on the page), a title, and any of:
 * paragraphs, a list of bullets, and a closing paragraph after the list.
 */
export const TERMS_VERSION = 'v1';
export const TERMS_UPDATED = '25 October 2026';

export const TERMS_SECTIONS = [
  {
    id: 'about',
    title: 'About these terms',
    paragraphs: [
      'These terms are an agreement between your institution and AfricaNenda for the use of the AfricaNenda IIPS '
        + 'Sandbox (the Sandbox). The person who applies accepts them on the institution\'s behalf, and confirms '
        + 'that they are allowed to do so.',
      'They apply to everyone at your institution who uses the Sandbox, through the portal or through its APIs.',
    ],
  },
  {
    id: 'sandbox',
    title: 'What the Sandbox is',
    paragraphs: [
      'The Sandbox is a test environment. Payment service providers use it to try their integrations on the FSPIOP, '
        + 'ISO 20022, ISO 8583 card and cross-border corridors, and to be assessed for certification.',
      'It is not a payment system. No real money moves, nothing settles, and no message sent to the Sandbox has any '
        + 'effect outside it.',
    ],
  },
  {
    id: 'test-data',
    title: 'Test data only',
    bullets: [
      'Use made-up data only. Never send real customer names, account or phone numbers, national ids or other '
        + 'personal data.',
      'Never send real card numbers, PINs, card verification codes or track data. Use the test cards the Sandbox '
        + 'provides.',
      'Use keys and certificates made for testing. Never upload a private key, certificate or password that you use '
        + 'in production.',
      'If you send real data by mistake, tell AfricaNenda straight away so that it can be removed.',
    ],
  },
  {
    id: 'credentials',
    title: 'Your account and credentials',
    bullets: [
      'Keep your passwords, API keys and private keys secret. Anyone who holds your API key can act as your '
        + 'institution in the Sandbox.',
      'You are responsible for what happens under your account, including what the colleagues you add as users do. '
        + 'Remove users who no longer need access.',
      'If a credential is lost or exposed, tell AfricaNenda, and it will issue a new one.',
    ],
  },
  {
    id: 'fair-use',
    title: 'Fair use',
    paragraphs: ['Do not:'],
    bullets: [
      'try to reach another participant\'s data, or use the Sandbox to pass yourself off as another institution;',
      'probe, attack or try to get around the Sandbox\'s security, or test it for vulnerabilities, without '
        + 'AfricaNenda\'s written agreement;',
      'put the Sandbox under heavy load, other than with its own performance tools or with AfricaNenda\'s agreement;',
      'use the Sandbox for anything other than developing and testing payment integrations.',
    ],
    after: 'AfricaNenda may limit how many requests you send, to keep the Sandbox usable for everyone.',
  },
  {
    id: 'certification',
    title: 'Certification',
    bullets: [
      'A certification report and certificate say that your integration passed the Sandbox\'s test cases, on the '
        + 'dates shown, against the Sandbox\'s own implementation of each scheme.',
      'They are not a licence, a regulatory approval, or permission to join any live payment system. They do not '
        + 'guarantee how your system will behave in production.',
      'An AfricaNenda certification officer decides whether to approve each report. AfricaNenda may revoke a '
        + 'certificate, for example if it was issued in error or the tests behind it no longer apply.',
      'Each certificate has a public check page, reached from its QR code. It shows your institution\'s name, its '
        + 'FSP id, the corridor, the verdict, who decided, and the dates. Anyone who has the certificate can open it.',
    ],
  },
  {
    id: 'data',
    title: 'How AfricaNenda handles your data',
    paragraphs: ['What AfricaNenda collects:'],
    bullets: [
      'the details in your application, including your contact\'s name and email address;',
      'the names and email addresses of your users;',
      'the messages your systems send to and receive from the Sandbox, activity logs, and the IP addresses that '
        + 'requests come from;',
      'the keys and certificates you register, and your test results, reports and certificates.',
    ],
    more: [
      {
        lead: 'How AfricaNenda uses it:',
        bullets: [
          'only to run, secure and support the Sandbox, and to assess certification;',
          'the AfricaNenda staff who run the Sandbox, and its certification officers, can see your test traffic and '
            + 'results; other participants cannot;',
          'AfricaNenda does not sell your data, and shares it outside AfricaNenda only with service providers that '
            + 'help run the Sandbox, under a duty of confidentiality, or where the law requires it;',
          'AfricaNenda may publish figures about the Sandbox as a whole, such as how many participants are '
            + 'certified, that do not identify your institution;',
          'passwords are stored only in a form that cannot be read back.',
        ],
      },
      {
        lead: 'How long AfricaNenda keeps it:',
        bullets: [
          'test traffic and logs may be deleted at any time, including when the Sandbox is reset;',
          'when your participation ends, your account and its test data are removed;',
          'certification reports, certificates and the decisions on them are kept, so that issued certificates can '
            + 'still be checked.',
        ],
      },
    ],
    after: 'To ask what AfricaNenda holds about you, or to have it corrected or deleted, email AfricaNenda.',
  },
  {
    id: 'availability',
    title: 'Availability and changes to the Sandbox',
    bullets: [
      'The Sandbox is provided as it is, and when it is available. AfricaNenda may change, pause or reset it, add '
        + 'or remove features, or change the test cases. It will try to give notice of changes that affect you.',
      'A reset can remove test data. Keep your own copy of anything you need.',
    ],
  },
  {
    id: 'ending',
    title: 'Ending your participation',
    bullets: [
      'You can stop using the Sandbox at any time. Email AfricaNenda to close your account.',
      'AfricaNenda may suspend or close your account if you break these terms, if your use puts the Sandbox or '
        + 'other participants at risk, or if the Sandbox closes. Where it can, it will tell you first and say why.',
    ],
  },
  {
    id: 'ownership',
    title: 'Ownership',
    bullets: [
      'The Sandbox, its software, test cases and documentation belong to AfricaNenda or its licensors. You may use '
        + 'them only to develop and test your integrations.',
      'Your own systems and software stay yours. If you send AfricaNenda feedback, it may use it to improve the '
        + 'Sandbox without any obligation to you.',
    ],
  },
  {
    id: 'responsibility',
    title: 'Responsibility',
    bullets: [
      'As far as the law allows, AfricaNenda makes no promises about the Sandbox beyond those in these terms, and '
        + 'is not liable for any loss arising from its use, including lost data, lost time, or decisions made on '
        + 'the basis of test results.',
      'Your institution is responsible for any harm caused by its use of the Sandbox in breach of these terms.',
    ],
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    paragraphs: [
      'AfricaNenda may update these terms. Each version has a number, and the version your institution accepted is '
        + 'recorded with its application. AfricaNenda will email the contacts on your account before a new version '
        + 'applies. If you keep using the Sandbox after that, you accept the new version.',
    ],
  },
];
